import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    HostListener,
    inject,
    input,
    Input,
    OnDestroy,
    OnInit,
    output
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { ActionItem, ActionItemStyle, IconSize, ShActionItemComponent } from '../../../../elements/public_api';
import { scrollToTop } from '../../../../helpers/scroll-to-top.helper';
import { RESULTS_PER_PAGE_OPTIONS } from '../display-settings-bar/entities/display-mode.entity';
import { PaginationInfo, RequestPaginationInfo, ResponsePaginationInfo } from './entities/pagination-info.entity';

@Component({
    standalone: true,
    selector: 'sh-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, ShActionItemComponent, TranslateModule]
})
export class ShPaginationComponent implements OnInit, OnDestroy {
    // Cap number of total results because of heavy processing required
    private readonly MAX_RESULTS_LIMIT: number = 15000;
    // Grace period so stepping through a few pages in a row results in a single page change
    private readonly STEP_DEBOUNCE_MS: number = 350;
    private readonly destroy$: Subject<void> = new Subject<void>();
    private readonly requestedPage$: Subject<number> = new Subject<number>();
    private readonly translateService: TranslateService = inject(TranslateService);

    // Static ids would clash (and break the label/select or label/input association) whenever
    // multiple sh-pagination instances are mounted at once, e.g. a dialog opened on top of a list
    private static instanceCount: number = 0;
    private readonly instanceId: number = ShPaginationComponent.instanceCount++;
    readonly resultsPerPageSelectId: string = `sh-pagination-per-page-${this.instanceId}`;
    readonly goToPageInputId: string = `sh-pagination-goto-input-${this.instanceId}`;

    resultsPerPageControl: UntypedFormControl = new UntypedFormControl();
    goToPageControl: UntypedFormControl = new UntypedFormControl('');

    /** Page we already emitted, as long as the incoming pagination info has not caught up yet */
    private emittedPage?: number;

    /** Inputs */
    paginationInfo = input.required<ResponsePaginationInfo | undefined>();

    @Input() previousPageMsg = '';
    @Input() nextPageMsg = '';
    @Input() perPageMsg = '';
    @Input() resultsPerPageOptions: number[] = RESULTS_PER_PAGE_OPTIONS;
    @Input() scrollRef?: string; // Ability to overwrite default scrollTo

    /** Computed */
    $isFirstPage = computed(() => !this.hasPreviousPage(this.paginationInfo()));
    $isLastPage = computed(() => !this.hasNextPage(this.paginationInfo()));
    $previousPageActionItem = computed(() => this.getPreviousActionItem(this.$isFirstPage()));
    $nextPageActionItem = computed(() => this.getNextActionItem(this.$isLastPage()));

    $currentPage = computed(() => this.paginationInfo()?.page ?? 0);
    $maxPage = computed(() => this.calculateMaxPage(this.paginationInfo()));
    $totalPages = computed(() => this.$maxPage() + 1);

    $rangeFrom = computed(() => this.calculateRangeFrom(this.paginationInfo()));
    $rangeTo = computed(() => this.calculateRangeTo(this.paginationInfo()));
    $rangeMsg = computed(() => this.getRangeMsg(this.paginationInfo()));
    $pageOfTotalMsg = computed(() =>
        this.translateService.instant('components.pagination.page-of-total', {
            page: this.$currentPage() + 1,
            totalPages: this.$totalPages()
        })
    );

    /** Outputs */
    paginationInfoChanged = output<RequestPaginationInfo>();

    /** Listeners */
    @HostListener('document:keydown.ArrowLeft', ['$event'])
    handleArrowLeft(event: KeyboardEvent) {
        // MED-6189: Only do if not targeting anything else
        if ((event.target as HTMLElement).nodeName === 'BODY') {
            this.onPreviousPage();
        }
    }

    @HostListener('document:keydown.ArrowRight', ['$event'])
    handleArrowRight(event: KeyboardEvent) {
        // MED-6189: Only do if not targeting anything else
        if ((event.target as HTMLElement).nodeName === 'BODY') {
            this.onNextPage();
        }
    }

    constructor() {
        effect(() => {
            this.selectMatchinPageSizeOption(this.paginationInfo());
            this.preventOutOfBoundsNavigation(this.paginationInfo(), this.$maxPage());
            this.showCurrentPageInGoToControl(this.$currentPage());
        });
    }

    ngOnInit(): void {
        this.initResultsPerPageListeners();
        this.initRequestedPageListener();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initResultsPerPageListeners(): void {
        this.resultsPerPageControl.valueChanges
            .pipe(
                map(value => value),
                takeUntil(this.destroy$)
            )
            .subscribe(size => {
                const sizeNumber: number = Number.parseInt(size, 10);
                const newPaginationInfo: PaginationInfo = {
                    ...this.paginationInfo(),
                    resultsPerPage: sizeNumber,
                    page: 0
                };
                this.emit(newPaginationInfo);
            });
    }

    private initRequestedPageListener(): void {
        this.requestedPage$
            .pipe(debounceTime(this.STEP_DEBOUNCE_MS), takeUntil(this.destroy$))
            .subscribe(page => this.navigateToPage(page));
    }

    /**
     * `label` still carries the previous/next message — {@link ShBaseActionItemComponent}'s `title$` derives
     * the native tooltip from it regardless of style, so the pure-icon style alone drops the visible text
     * without losing that accessible name. `PureSquareIconButton` keeps the square footprint (40px, matching
     * the go-to-page input's height) of the saved-queries button while staying ghost/transparent rather than
     * that button's light background.
     */
    private getPreviousActionItem(isFirst: boolean): ActionItem {
        return {
            label: this.previousPageMsg,
            eventListener: event => this.onPreviousPage(),
            icon: 'arrow-left',
            iconSize: IconSize.SMALL,
            style: ActionItemStyle.PureSquareIconButton,
            disabled: isFirst
        };
    }

    private getNextActionItem(isLast: boolean): ActionItem {
        return {
            label: this.nextPageMsg,
            eventListener: event => this.onNextPage(),
            icon: 'arrow-right',
            iconSize: IconSize.SMALL,
            style: ActionItemStyle.PureSquareIconButton,
            disabled: isLast
        };
    }

    // #region Effects
    private selectMatchinPageSizeOption(pagination: PaginationInfo | undefined): void {
        const resultsPerPage = pagination?.resultsPerPage || this.resultsPerPageOptions[0];

        if (!this.resultsPerPageOptions.includes(resultsPerPage)) {
            this.resultsPerPageOptions.push(resultsPerPage);
        }
        this.resultsPerPageOptions = this.resultsPerPageOptions.sort((a, b) => a - b);
        this.resultsPerPageControl.setValue(resultsPerPage, { emitEvent: false });
    }

    private preventOutOfBoundsNavigation(pagination: PaginationInfo | undefined, maxPage: number): void {
        if (!pagination) {
            return;
        }

        if (pagination.page < 0 || pagination.page > maxPage) {
            this.navigateToPage(0);
        }
    }
    // #endregion

    // #region Actions
    private onPreviousPage(): void {
        const isFirstPage = this.$isFirstPage();

        if (isFirstPage) {
            return;
        }

        this.navigateToPage(this.$currentPage() - 1);
    }

    private onNextPage(): void {
        const isLastPage = this.$isLastPage();

        if (isLastPage) {
            return;
        }

        this.navigateToPage(this.$currentPage() + 1);
    }

    /** Navigates to the page shown in the field straight away (enter, blur) */
    public goToPage(): void {
        this.navigateToPage(this.getShownPage());
    }

    /** Navigates to the page shown in the field once the steps settle (native spinner arrows) */
    public queueGoToPage(): void {
        const targetPage: number = this.getShownPage();

        this.syncGoToPageControl(targetPage);
        this.requestedPage$.next(targetPage);
    }

    /** Steps the field with the arrow up/down keys, taking over the native step to keep both in sync */
    public stepPage(step: number, event: Event): void {
        event.preventDefault();

        const targetPage: number = this.clampPage(this.getShownPage() + step);

        this.syncGoToPageControl(targetPage);
        this.requestedPage$.next(targetPage);
    }

    public navigateToPage(page: number): void {
        const pagination = this.paginationInfo();

        page = this.clampPage(page);

        if (!pagination) {
            return;
        }

        this.syncGoToPageControl(page);

        // Already on that page, or a request for it is still on its way
        if (page === (this.emittedPage ?? this.$currentPage())) {
            return;
        }

        this.emittedPage = page;
        this.emit({ ...pagination, page });
    }

    private emit(paginationInfo: PaginationInfo): void {
        // MED-1959: When clicking on previous and next, scrollbar did not go up
        // (partly cause it got held back by .sh-list-layout__list (overflow-y: auto))

        if (this.scrollRef) {
            setTimeout(() => scrollToTop(document.querySelector(this.scrollRef!)), 100);
        } else {
            setTimeout(() => scrollToTop(document.querySelector('.sh-list-layout__list')), 100);
            setTimeout(() => scrollToTop(document.querySelector('.sh-scroll-container')), 100);
        }
        this.paginationInfoChanged.emit(paginationInfo);
    }
    // #endregion

    // #region Utils
    /** The incoming pagination info is leading again, so drop the page we were waiting for */
    private showCurrentPageInGoToControl(page: number): void {
        this.emittedPage = undefined;
        this.syncGoToPageControl(page);
    }

    /** Field is 1-indexed for the user, internal page numbers are 0-indexed */
    private syncGoToPageControl(page: number): void {
        this.goToPageControl.setValue(page + 1, { emitEvent: false });
    }

    /** The page the field is showing, which can still be ahead of the applied pagination info */
    private getShownPage(): number {
        const shownPage: number = Number.parseInt(this.goToPageControl.value, 10);

        if (Number.isNaN(shownPage)) {
            return this.$currentPage();
        }

        // Field is 1-indexed for the user, internal page numbers are 0-indexed
        return this.clampPage(shownPage - 1);
    }

    private clampPage(page: number): number {
        const maxPage = this.$maxPage();

        // Prevent navigation to negative page
        page = page < 0 ? 0 : page;
        // Prevent navigation beyond max page
        return page > maxPage ? maxPage : page;
    }

    private hasNextPage(pi: ResponsePaginationInfo | undefined): boolean {
        return !!pi && pi.page < this.$maxPage();
    }

    private hasPreviousPage(pi: PaginationInfo | undefined): boolean {
        return !!pi && pi.page !== 0;
    }

    private calculateMaxPage(pi: ResponsePaginationInfo | undefined): number {
        const resultsPerPage = pi?.resultsPerPage ?? 10;

        const totalNumberOfResults = pi?.totalNumberOfResults ?? 0;

        const results = totalNumberOfResults > this.MAX_RESULTS_LIMIT ? this.MAX_RESULTS_LIMIT : totalNumberOfResults;

        const totalPages = Math.ceil(results / resultsPerPage);

        return totalPages > 0 ? totalPages - 1 : 0;
    }

    private calculateRangeFrom(pi: ResponsePaginationInfo | undefined): number {
        if (!pi) {
            return 0;
        }
        return Math.min(pi.page * pi.resultsPerPage + 1, pi.totalNumberOfResults);
    }

    private calculateRangeTo(pi: ResponsePaginationInfo | undefined): number {
        if (!pi) {
            return 0;
        }
        return Math.min(pi.page * pi.resultsPerPage + pi.resultsPerPage, pi.totalNumberOfResults);
    }

    private getRangeMsg(pi: ResponsePaginationInfo | undefined): string {
        if (!pi) {
            return '';
        }
        return `${this.$rangeFrom()}-${this.$rangeTo()} ${this.translateService.instant('ui.from')} ${
            pi.totalNumberOfResults
        }`;
    }
    // #endregion
}
